import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { subscribeRequests } from '../../data/listeners/requests';
import { subscribeLeads } from '../../data/listeners/leads';
import {
  updateRequestStatus, setRequestRemark, createRequest, validateDecision,
} from '../../data/commands/requestCommands';
import { requestsByStatus } from '../../data/pipeline';
import {
  REQUEST_STATUSES, REQUEST_STATUS_LABEL, REQUEST_STATUS_COLOR,
} from '../../data/leadConstants';
import Icon from '../../components/Icon';

const CAN_APPROVE_ROLES = ['manager', 'owner', 'hqadmin'];

const fmt = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—');

function RequestModal({ leads, onClose, actor, tenantId }) {
  const [leadId, setLeadId] = useState(leads[0]?.id ?? '');
  const [type, setType] = useState('request');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The leads listener may not have populated when the modal first mounts (its
  // initial leadId would then be '' while the <select> visually shows the first
  // option). Sync to the first lead once leads arrive so the default matches.
  useEffect(() => {
    if (!leadId && leads.length) setLeadId(leads[0].id);
  }, [leads, leadId]);

  async function save() {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) { setError('Pick a customer / lead.'); return; }
    if (!text.trim()) { setError('Describe the request or feedback.'); return; }
    setSaving(true); setError('');
    try {
      await createRequest({
        tenantId, leadId, territoryId: lead.territoryId, customerName: lead.businessName, type, text,
      }, actor);
      onClose();
    } catch (e) {
      setError(e.message || 'Could not log the request.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header><Icon name="requests" size={18} /> Log request / feedback</header>
        <div className="body">
          <div className="field"><label>Customer / lead</label>
            <select className="input" value={leadId} onChange={(e) => setLeadId(e.target.value)}>
              {leads.map((l) => <option key={l.id} value={l.id}>{l.businessName}</option>)}
            </select>
          </div>
          <div className="field"><label>Type</label>
            <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="request">Request</option><option value="feedback">Feedback</option>
            </select>
          </div>
          <div className="field"><label>Details</label>
            <textarea className="input" style={{ minHeight: 64, width: '100%' }} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Add a Sunday pickup slot" />
          </div>
          {error && <div className="form-error">{error}</div>}
        </div>
        <footer>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Logging…' : 'Log request'}</button>
        </footer>
      </div>
    </div>
  );
}

// Requests & feedback — 4-column manager-approval flow (DoR-29). Cards open the
// shared Lead Drawer; managers Approve/Reject with a remark, agents annotate only.
export default function Requests() {
  const { tenantId, role, territoryIds, profile, authUser } = useAuth();
  const { openLead } = useUI();
  const [requests, setRequests] = useState([]);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(null);
  const [view, setView] = useState('kanban'); // 'kanban' | 'table'
  const [showNew, setShowNew] = useState(false);
  const [remarks, setRemarks] = useState({}); // requestId → draft remark text

  useEffect(() => subscribeRequests({ tenantId, role, territoryIds }, setRequests, (e) => setError(e.message || 'Could not load requests.')), [tenantId, role, territoryIds]);
  useEffect(() => subscribeLeads({ tenantId, role, territoryIds }, setLeads), [tenantId, role, territoryIds]);

  const cols = useMemo(() => requestsByStatus(requests), [requests]);
  const actor = { uid: authUser?.uid, name: profile?.name ?? profile?.email, role };
  const canApprove = CAN_APPROVE_ROLES.includes(role);
  const remarkFor = (r) => (remarks[r.id] !== undefined ? remarks[r.id] : (r.remark ?? ''));

  async function decide(r, status) {
    const remark = remarkFor(r);
    const v = validateDecision(status, remark);
    if (!v.ok) { setError(v.error); return; }
    setError('');
    try {
      await updateRequestStatus(r.id, status, remark, actor);
    } catch (err) {
      setError(err.message || 'Could not update the request.');
    }
  }

  async function saveRemark(r) {
    try {
      await setRequestRemark(r.id, remarkFor(r), actor);
    } catch (err) {
      setError(err.message || 'Could not save the remark.');
    }
  }

  async function onDrop(status, e) {
    e.preventDefault();
    setDragOver(null);
    if (!canApprove) return; // only managers move cards (rules also enforce this)
    const id = e.dataTransfer.getData('text/plain');
    const req = requests.find((r) => r.id === id);
    if (!req || req.status === status) return;
    // Dragging into Approved/Rejected is a decision too — it needs feedback.
    const v = validateDecision(status, req.remark);
    if (!v.ok) { setError(v.error); return; }
    setError('');
    try {
      await updateRequestStatus(id, status, req.remark, actor);
    } catch (err) {
      setError(err.message || 'Could not update the request.');
    }
  }

  return (
    <div>
      <div className="toolbar">
        <div className="seg" role="group" aria-label="Requests view">
          <button type="button" className={view === 'kanban' ? 'on' : ''} onClick={() => setView('kanban')}>Kanban</button>
          <button type="button" className={view === 'table' ? 'on' : ''} onClick={() => setView('table')}>Table</button>
        </div>
        <span style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={() => setShowNew(true)}><Icon name="plus" size={15} /> Log request</button>
      </div>

      {error && <div className="form-error">{error}</div>}

      {view === 'kanban' ? (
        <div className="kboard">
          {REQUEST_STATUSES.map((status) => (
            <div
              key={status}
              className={`kcol${dragOver === status ? ' dragover' : ''}`}
              style={{ '--kcol-color': REQUEST_STATUS_COLOR[status] }}
              onDragOver={(e) => { if (canApprove) { e.preventDefault(); setDragOver(status); } }}
              onDragLeave={() => setDragOver((s) => (s === status ? null : s))}
              onDrop={(e) => onDrop(status, e)}
            >
              <div className="kcol-head"><span>{REQUEST_STATUS_LABEL[status]}</span><span className="ct">{cols[status].length}</span></div>
              <div className="kcol-body">
                {cols[status].map((r) => {
                  const pending = status === 'received' || status === 'pending';
                  return (
                    <div
                      key={r.id}
                      className="kcard"
                      style={{ cursor: r.leadId ? 'pointer' : 'default' }}
                      draggable={canApprove}
                      onDragStart={(e) => e.dataTransfer.setData('text/plain', r.id)}
                      onClick={() => r.leadId && openLead(r.leadId)}
                      title={r.leadId ? 'Open account history' : undefined}
                    >
                      <div className="biz">{r.customerName}</div>
                      <span className="badge tier-Small" style={{ margin: '4px 0' }}>{r.type}</span>
                      <div style={{ fontSize: 'var(--text-small)' }}>{r.text}</div>
                      {r.remark && <div className="meta" style={{ marginTop: 4 }}><b>Remark:</b> {r.remark}</div>}
                      <div className="meta" style={{ marginTop: 4 }}>{r.id.slice(0, 8)} · {fmt(r.createdAt)}{r.decidedByName ? ` · ${r.decidedByName}` : ''}</div>

                      {pending && (
                        // Stop propagation so editing the remark / clicking a button
                        // doesn't also open the drawer.
                        <div className="req-actions" onClick={(e) => e.stopPropagation()}>
                          <textarea
                            className="input"
                            rows={2}
                            placeholder={canApprove ? 'Decision remark / feedback…' : 'Add a remark…'}
                            value={remarkFor(r)}
                            onChange={(e) => setRemarks((m) => ({ ...m, [r.id]: e.target.value }))}
                          />
                          <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                            {canApprove ? (
                              <>
                                <button type="button" className="btn btn-sm" style={{ background: REQUEST_STATUS_COLOR.approved, color: '#fff' }} onClick={() => decide(r, 'approved')}>Approve</button>
                                <button type="button" className="btn btn-sm" style={{ background: REQUEST_STATUS_COLOR.rejected, color: '#fff' }} onClick={() => decide(r, 'rejected')}>Reject</button>
                              </>
                            ) : (
                              <button type="button" className="btn btn-ghost btn-sm" onClick={() => saveRemark(r)}>Save remark</button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Ref</th><th>Customer</th><th>Type</th><th>Request / feedback</th><th>Status</th><th>Remark</th><th>Date</th></tr></thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} className={r.leadId ? 'click' : ''} onClick={() => r.leadId && openLead(r.leadId)}>
                  <td className="muted">{r.id.slice(0, 8)}</td>
                  <td style={{ fontWeight: 'var(--font-semibold)' }}>{r.customerName}</td>
                  <td>{r.type}</td>
                  <td>{r.text}</td>
                  <td>
                    <span className="badge" style={{ background: `${REQUEST_STATUS_COLOR[r.status]}1a`, color: REQUEST_STATUS_COLOR[r.status] }}>
                      <span className="dot" style={{ background: REQUEST_STATUS_COLOR[r.status] }} />{REQUEST_STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="muted">{r.remark || '—'}</td>
                  <td className="muted">{fmt(r.createdAt)}</td>
                </tr>
              ))}
              {requests.length === 0 && <tr><td colSpan={7} style={{ color: 'var(--color-fg-muted)', textAlign: 'center', padding: 'var(--space-8)' }}>No requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <RequestModal leads={leads} onClose={() => setShowNew(false)} actor={actor} tenantId={tenantId} />}
    </div>
  );
}
